/**
 * Game Recorder — records every move during a game for later replay/analysis.
 *
 * Format (v1):
 * {
 *   version: 1,
 *   initialState: {
 *     gameBoard:      2D array (the map before any moves),
 *     players:        [{ name, colorIndex, isAI, aiDifficulty }],
 *     hands:          [[{colorIndex}, ...], ...],   // starting hand per player
 *     markersPool:    [{colorIndex}, ...],           // pool in draw order
 *     startingPlayer: number                         // index of first mover
 *   },
 *   moves: [
 *     { type: 'place', player: 0, colorIndex: 1, row: 3, col: 7 },
 *     { type: 'swap',  player: 2, target: 0 },
 *     ...
 *   ]
 * }
 *
 * With initialState + moves the entire game can be deterministically replayed
 * (draw from pool is always FIFO, swap is whole-hand).
 */

const CURRENT_VERSION = 1;

/**
 * Create a new game log snapshot at the beginning of a game.
 *
 * @param {Array<Array<string>>} gameBoard  – the initial board (post map-gen)
 * @param {Array<Object>}        players   – full player objects (after dealing)
 * @param {Array<Object>}        markersPool – remaining pool after dealing
 * @param {number}               startingPlayer – index of the first player
 * @returns {Object} a mutable gameLog object — keep it in a ref and pass to
 *                   recordMove() on every turn, then to finalizeGameLog() at
 *                   game end.
 */
export function createGameLog(gameBoard, players, markersPool, startingPlayer) {
  return {
    version: CURRENT_VERSION,
    initialState: {
      // Deep-copy the board so later mutations don't affect the snapshot
      gameBoard: gameBoard.map(row => [...row]),
      // Strip runtime fields — keep only what's needed for replay
      players: players.map(p => ({
        name: p.name,
        colorIndex: p.colorIndex,
        isAI: !!p.isAI,
        ...(p.isAI && p.aiDifficulty ? { aiDifficulty: p.aiDifficulty } : {})
      })),
      // Deep-copy hands per player
      hands: players.map(p => (p.markers || []).map(m => ({ colorIndex: m.colorIndex }))),
      // Deep-copy the pool
      markersPool: markersPool.map(m => ({ colorIndex: m.colorIndex })),
      startingPlayer
    },
    moves: []
  };
}

/**
 * Record a "place" move.
 *
 * @param {Object} gameLog      – the mutable gameLog from createGameLog()
 * @param {number} playerIndex  – whose turn it is
 * @param {number} colorIndex   – colorIndex of the placed marker (marker.colorIndex)
 * @param {number} row          – board row
 * @param {number} col          – board column
 */
export function recordPlaceMove(gameLog, playerIndex, colorIndex, row, col) {
  if (!gameLog) return;
  gameLog.moves.push({ type: 'place', player: playerIndex, colorIndex, row, col });
}

/**
 * Record a "swap" move.
 *
 * @param {Object} gameLog      – the mutable gameLog from createGameLog()
 * @param {number} playerIndex  – whose turn it is
 * @param {number} targetPlayer – index of the player swapped with
 */
export function recordSwapMove(gameLog, playerIndex, targetPlayer) {
  if (!gameLog) return;
  gameLog.moves.push({ type: 'swap', player: playerIndex, target: targetPlayer });
}

/**
 * Compact the board encoding for storage.
 * Converts the 2D array into a flat string:  B/X/Y stay as-is, numbers 0-7 stay.
 * This reduces JSONB size by ~60 % compared to nested arrays of strings.
 *
 * @param {Array<Array>} board
 * @returns {{ rows: number, cols: number, data: string }}
 */
function compactBoard(board) {
  const rows = board.length;
  const cols = board[0]?.length || 0;
  // Each cell encoded as one char: B, X, Y, or digit 0-7
  let data = '';
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = board[r][c];
      data += typeof cell === 'number' ? String(cell) : cell;
    }
  }
  return { rows, cols, data };
}

/**
 * Compact the markers pool for storage.
 * Instead of [{colorIndex:2},{colorIndex:0},...] store "20..." (one digit per marker).
 *
 * @param {Array<{colorIndex:number}>} pool
 * @returns {string}
 */
function compactMarkers(pool) {
  return pool.map(m => String(m.colorIndex)).join('');
}

/**
 * Prepare the game log for database storage (compacted).
 * Call this right before saving — it returns a new object, does not mutate.
 *
 * @param {Object} gameLog
 * @returns {Object} compacted version ready for JSONB storage
 */
export function finalizeGameLog(gameLog) {
  if (!gameLog) return null;
  return {
    v: gameLog.version,
    i: {
      b: compactBoard(gameLog.initialState.gameBoard),
      p: gameLog.initialState.players,
      h: gameLog.initialState.hands.map(compactMarkers),
      m: compactMarkers(gameLog.initialState.markersPool),
      s: gameLog.initialState.startingPlayer
    },
    // Moves are already compact; just shorten key names
    moves: gameLog.moves.map(move => {
      if (move.type === 'place') {
        return { t: 'p', p: move.player, ci: move.colorIndex, r: move.row, c: move.col };
      }
      // swap
      return { t: 's', p: move.player, tp: move.target };
    })
  };
}

// ---------------------------------------------------------------------------
// Replay helpers (expansion — useful for future bot training / UI replay)
// ---------------------------------------------------------------------------

/**
 * Expand a compacted board string back to 2D array.
 */
export function expandBoard({ rows, cols, data }) {
  const board = [];
  let idx = 0;
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) {
      const ch = data[idx++];
      row.push(/\d/.test(ch) ? Number(ch) : ch);
    }
    board.push(row);
  }
  return board;
}

/**
 * Expand a compact marker string back to array of { colorIndex }.
 */
export function expandMarkers(str) {
  return str.split('').map(ch => ({ colorIndex: Number(ch) }));
}

/**
 * Expand a compacted game log back to the full verbose format.
 */
export function expandGameLog(compact) {
  if (!compact) return null;
  return {
    version: compact.v,
    initialState: {
      gameBoard: expandBoard(compact.i.b),
      players: compact.i.p,
      hands: compact.i.h.map(expandMarkers),
      markersPool: expandMarkers(compact.i.m),
      startingPlayer: compact.i.s
    },
    moves: compact.moves.map(m => {
      if (m.t === 'p') {
        // Accept both compact encodings: ci = recorded color, mi = hand index.
        const move = { type: 'place', player: m.p, row: m.r, col: m.c };
        if (m.ci !== undefined) move.colorIndex = m.ci;
        if (m.mi !== undefined) move.markerIndex = m.mi;
        return move;
      }
      return { type: 'swap', player: m.p, target: m.tp };
    })
  };
}

// ---------------------------------------------------------------------------
// Replay stepper — deterministically replays a game move by move
// ---------------------------------------------------------------------------

/**
 * Place a marker on the board and open adjacent cells.
 * Pure function — returns a new board (does not mutate input).
 */
function replayPlaceMarker(board, row, col, colorIndex) {
  const newBoard = board.map(r => [...r]);
  newBoard[row][col] = colorIndex;
  const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  for (const [dy, dx] of directions) {
    const ny = row + dy;
    const nx = col + dx;
    if (ny >= 0 && ny < newBoard.length && nx >= 0 && nx < newBoard[0].length) {
      if (newBoard[ny][nx] === 'X') {
        newBoard[ny][nx] = 'Y';
      }
    }
  }
  return newBoard;
}

/**
 * Build the game state at a given move index (0 = initial, 1 = after first move, etc.).
 *
 * @param {Object} gameLog – expanded (verbose) game log from expandGameLog()
 * @param {number} moveIndex – 0 for initial state, N for state after N-th move
 * @returns {{ gameBoard, players, hands, markersPool, currentPlayer, lastMove }}
 *   - players includes { name, colorIndex, isAI, aiDifficulty, score }
 *   - hands[i] = current hand of player i (array of { colorIndex })
 *   - lastMove = the move that was just executed (null for initial state)
 */
export function replayToMove(gameLog, moveIndex) {
  if (!gameLog) return null;
  const { initialState, moves } = gameLog;

  // Deep-copy initial state
  let board = initialState.gameBoard.map(r => [...r]);
  let hands = initialState.hands.map(h => h.map(m => ({ ...m })));
  let pool = initialState.markersPool.map(m => ({ ...m }));
  let currentPlayer = initialState.startingPlayer;

  // Calculate score for a player on the current board
  const calcScore = (b, colorIndex) => {
    let score = 0;
    const visited = new Set();
    for (let i = 0; i < b.length; i++) {
      for (let j = 0; j < b[0].length; j++) {
        const key = `${i},${j}`;
        if (!visited.has(key) && b[i][j] === colorIndex) {
          let islandSize = 0;
          const stack = [[i, j]];
          while (stack.length > 0) {
            const [x, y] = stack.pop();
            const posKey = `${x},${y}`;
            if (visited.has(posKey)) continue;
            visited.add(posKey);
            if (b[x][y] === colorIndex) {
              islandSize += 1;
              for (const [di, dj] of [[-1,0],[1,0],[0,-1],[0,1]]) {
                const ni = x + di, nj = y + dj;
                if (ni >= 0 && ni < b.length && nj >= 0 && nj < b[0].length && b[ni][nj] === colorIndex) {
                  stack.push([ni, nj]);
                }
              }
            }
          }
          if (islandSize === 2) score += 1;
          else if (islandSize === 3) score += 4;
          else if (islandSize >= 4) score += 7 + (islandSize - 4) * 2;
        }
      }
    }
    return score;
  };

  // Find the next active player (has markers in hand)
  const getNextActive = (cur) => {
    const count = hands.length;
    let next = (cur + 1) % count;
    let attempts = 0;
    while (hands[next].length === 0 && attempts < count) {
      next = (next + 1) % count;
      attempts++;
    }
    return attempts >= count ? cur : next;
  };

  let lastMove = null;
  const clamp = Math.min(moveIndex, moves.length);

  for (let i = 0; i < clamp; i++) {
    const move = moves[i];
    lastMove = move;

    if (move.type === 'place') {
      const playerHand = Array.isArray(hands[move.player]) ? hands[move.player] : null;

      if (typeof move.colorIndex === 'number') {
        // New format: colorIndex is recorded directly — always place on board.
        // The move was recorded because it happened; trust the data even if
        // the tracked hand diverged (e.g. due to a duplicate-recorded swap).
        board = replayPlaceMarker(board, move.row, move.col, move.colorIndex);

        if (playerHand) {
          const markerIdx = playerHand.findIndex(m => m.colorIndex === move.colorIndex);
          if (markerIdx >= 0) {
            playerHand.splice(markerIdx, 1);
          }
          if (pool.length > 0) {
            playerHand.push(pool[0]);
            pool = pool.slice(1);
          }
        }
      } else if (typeof move.markerIndex === 'number') {
        // Hand-index form: resolve the color from the replay hand state.
        const marker = playerHand?.[move.markerIndex];
        if (marker && typeof marker.colorIndex === 'number') {
          board = replayPlaceMarker(board, move.row, move.col, marker.colorIndex);
          playerHand.splice(move.markerIndex, 1);
          if (pool.length > 0) {
            playerHand.push(pool[0]);
            pool = pool.slice(1);
          }
        }
      }
    } else if (move.type === 'swap') {
      if (!Array.isArray(hands[move.player]) || !Array.isArray(hands[move.target])) {
        currentPlayer = getNextActive(move.player);
        continue;
      }
      const temp = hands[move.player];
      hands[move.player] = hands[move.target];
      hands[move.target] = temp;
    }

    currentPlayer = getNextActive(move.player);
  }

  // Calculate scores for all players
  const players = initialState.players.map((p, idx) => ({
    ...p,
    score: calcScore(board, p.colorIndex),
    markers: hands[idx]
  }));

  return { gameBoard: board, players, hands, markersPool: pool, currentPlayer, lastMove };
}

/**
 * Count how many own-color markers each player received from other players via swaps.
 *
 * For each swap, both participants may receive some of their own color from the
 * other hand. This helper replays hand changes only, so GameEnd insights can show
 * who benefited the most from swaps overall.
 *
 * @param {Object|null} gameLog – expanded game log from createGameLog()/expandGameLog()
 * @returns {number[]} totals by original player index
 */
export function getOwnMarkersGainedFromSwaps(gameLog) {
  if (!gameLog?.initialState?.players || !gameLog?.initialState?.hands) return [];

  const hands = gameLog.initialState.hands.map((hand) => hand.map((marker) => ({ ...marker })));
  const pool = gameLog.initialState.markersPool
    ? gameLog.initialState.markersPool.map((marker) => ({ ...marker }))
    : [];
  const gains = gameLog.initialState.players.map(() => 0);

  const drawFromPool = (playerIndex) => {
    if (!Array.isArray(hands[playerIndex]) || pool.length === 0) return;
    hands[playerIndex].push(pool.shift());
  };

  for (const move of gameLog.moves || []) {
    if (move?.type === 'place') {
      const playerHand = Array.isArray(hands[move.player]) ? hands[move.player] : null;
      if (!playerHand) continue;

      if (typeof move.colorIndex === 'number') {
        const markerIdx = playerHand.findIndex((marker) => marker.colorIndex === move.colorIndex);
        if (markerIdx >= 0) {
          playerHand.splice(markerIdx, 1);
        }
        drawFromPool(move.player);
      } else if (typeof move.markerIndex === 'number' && playerHand[move.markerIndex]) {
        playerHand.splice(move.markerIndex, 1);
        drawFromPool(move.player);
      }

      continue;
    }

    if (move?.type !== 'swap') continue;
    if (!Array.isArray(hands[move.player]) || !Array.isArray(hands[move.target])) continue;

    const playerColor = gameLog.initialState.players[move.player]?.colorIndex;
    const targetColor = gameLog.initialState.players[move.target]?.colorIndex;

    if (typeof playerColor === 'number') {
      gains[move.player] += hands[move.target].filter((marker) => marker.colorIndex === playerColor).length;
    }
    if (typeof targetColor === 'number') {
      gains[move.target] += hands[move.player].filter((marker) => marker.colorIndex === targetColor).length;
    }

    const tmp = hands[move.player];
    hands[move.player] = hands[move.target];
    hands[move.target] = tmp;
  }

  return gains;
}

/**
 * Count how many foreign-color markers each player placed during the game.
 *
 * This uses the replay log rather than live move metadata so it stays correct
 * even if the DB move record omitted or mis-reported the placed color.
 *
 * @param {Object|null} gameLog – expanded game log from createGameLog()/expandGameLog()
 * @returns {number[]} totals by original player index
 */
export function getForeignTilesPlacedCounts(gameLog) {
  if (!gameLog?.initialState?.players) return [];

  const ownColors = gameLog.initialState.players.map((player) => player.colorIndex);
  const counts = gameLog.initialState.players.map(() => 0);

  for (const move of gameLog.moves || []) {
    if (move?.type !== 'place' || typeof move.player !== 'number') continue;
    if (typeof move.colorIndex !== 'number') continue;
    if (ownColors[move.player] !== move.colorIndex) {
      counts[move.player] += 1;
    }
  }

  return counts;
}
